import type { PersonId } from '@shared/brand';
import type { DateRange } from '@domain/value-objects/DateRange';
import {
  CONFIRMED_ABSENCE_STATUSES,
  OCCUPYING_STATUSES,
  type RequestStatus,
} from '@domain/vocabulary';
import { formatDateRange } from '../../format';

/**
 * Quién del área estará fuera en las fechas que se piden, y por qué.
 *
 * El asistente ya bloqueaba a quien tuviera fechas cruzadas, pero sólo lo
 * preguntaba a las ausencias de la sub área propia: con «Todos», alguien de otra
 * sub área salía «disponible» aunque estuviera de vacaciones. Así se nombró a
 * Jessica respaldo de Luis Alberto el 26 oct 2026, con sus vacaciones del 23 al
 * 26 ya aprobadas. Esto recibe las del área entera.
 *
 * Sólo ve vacaciones, que son lo que el portal deja leer de un compañero. Las
 * incapacidades y los permisos de día completo los frena el servidor.
 */
export interface AbsenceLike {
  readonly personId: PersonId;
  readonly range: DateRange;
  readonly status: RequestStatus;
}

/** De cada persona fuera, la nota que se lee junto a su nombre. */
export const awayDuring = (
  absences: readonly AbsenceLike[],
  range: DateRange,
): ReadonlyMap<PersonId, string> => {
  const away = new Map<PersonId, string>();

  for (const absence of absences) {
    if (!OCCUPYING_STATUSES.includes(absence.status)) continue;
    if (!absence.range.overlaps(range)) continue;

    const confirmed = CONFIRMED_ABSENCE_STATUSES.includes(absence.status);
    const note = `${confirmed ? 'de vacaciones' : 'pidió vacaciones'} ${formatDateRange(absence.range)}`;

    // Aprobada gana a pedida: es lo que más seguro dice que no estará.
    if (!away.has(absence.personId) || confirmed) away.set(absence.personId, note);
  }

  return away;
};
